import React from 'react';
import { motion, useInView } from 'framer-motion';
import { DoodleGlass, DoodleSim } from './Hand';
import { DUR, EASE_OUT, useMotionOK } from '../motion';

export type LoopStage = { name: string; desc: string; angle: number };

export const LOOP_STAGES: LoopStage[] = [
    { name: 'Understands the task', desc: 'Identifies what success requires from the robot', angle: -90 },
    { name: 'Builds the training path', desc: 'Starts with simple cases and adds variation', angle: -50 },
    { name: 'Experiments in simulation', desc: 'Generates training experience safely at scale', angle: -10 },
    { name: 'Experiments on the robot', desc: 'Tests behavior and collects evidence on real hardware', angle: 30 },
    { name: 'Closes the sim-to-real gap', desc: 'Uses real-robot results to correct what simulation missed', angle: 70 },
    { name: 'Learns the missing behavior', desc: 'Turns demonstrations and corrections into better behavior', angle: 110 },
    { name: 'Optimizes for the hardware', desc: 'Makes the system run within the target device limits', angle: 150 },
    { name: 'Validates and deploys', desc: 'Tests the complete system on the robot and ships it', angle: 190 },
    { name: 'Monitors and improves', desc: 'Uses real-world performance to build and redeploy the next version', angle: 230 },
];

/* One full orbit of the accent arc. */
const ORBIT_MS = 12000;
/* The ring draws itself in before the arc starts travelling. */
const DRAW_MS = 900;
/* Each node lands roughly as the drawing head passes it. Kept short enough
   that every label is readable well inside a second. */
const NODE_MS = (i: number) => 150 + i * 100;
/* The centre line is the section's real content, so it does not wait for the
   ring. The doodles are decoration and come in last. */
const CENTER_MS = 500;
const DOODLE_MS = 800;

const CX = 50;
const CY = 50;
const R = 35;
/* Fraction of the ring the traveling accent head covers. */
const ARC_FRACTION = 0.11;
/* Radial gap between the ring and each stage label, in viewBox units. Side
   descriptions need more clearance than the compact label at twelve o'clock. */
const LABEL_OFFSETS = [10.5, 14, 14, 14, 14, 14, 14, 14, 14];

/* Fixed per-stage tilts. Random-looking, but stable across renders. */
const LABEL_TILT = [-1.6, 1.2, -0.8, 1.8, -2, 0.9, -1.3, 1.4, -0.6];

/* Where each stage sits along the path, as a fraction of one orbit. Sampling
   starts at the first stage (-90°), so this is also the arc head's parking spot. */
const STAGE_AT = LOOP_STAGES.map((s) => ((s.angle + 90) / 360 + 1) % 1);

/* A gently deformed circle. Two low harmonics keep the outline hand-drawn
   rather than geometric, while staying inside the square the labels ring. */
function radiusAt(deg: number) {
    const t = (deg * Math.PI) / 180;
    return R * (1 + 0.035 * Math.sin(3 * t + 0.6) + 0.02 * Math.cos(5 * t));
}

const RING_PATH = (() => {
    const samples = 180;
    const pts: string[] = [];
    for (let i = 0; i < samples; i += 1) {
        const deg = -90 + (360 * i) / samples;
        const r = radiusAt(deg);
        const a = (deg * Math.PI) / 180;
        pts.push(`${(CX + r * Math.cos(a)).toFixed(3)} ${(CY + r * Math.sin(a)).toFixed(3)}`);
    }
    return `M${pts[0]}L${pts.slice(1).join('L')}Z`;
})();

function polar(angleDeg: number, extra: number) {
    const r = radiusAt(angleDeg) + extra;
    const a = (angleDeg * Math.PI) / 180;
    return {
        left: `${CX + r * Math.cos(a)}%`,
        top: `${CY + r * Math.sin(a)}%`,
    };
}

function stageAtProgress(p: number) {
    let found = 0;
    for (let i = 0; i < STAGE_AT.length; i += 1) if (p >= STAGE_AT[i]) found = i;
    return found;
}

/* Shortest signed distance between two points on a loop. */
function wrapDelta(from: number, to: number) {
    const d = ((to - from) % 1 + 1.5) % 1 - 0.5;
    return d;
}

export function LoopRing() {
    const ref = React.useRef<HTMLDivElement>(null);
    const trackRef = React.useRef<SVGPathElement>(null);
    const arcRef = React.useRef<SVGPathElement>(null);
    const inView = useInView(ref, { margin: '-120px' });
    const motionOK = useMotionOK();
    const [entered, setEntered] = React.useState(false);
    const [drawn, setDrawn] = React.useState(false);
    const [active, setActive] = React.useState(0);
    const [hover, setHover] = React.useState<number | null>(null);
    const [total, setTotal] = React.useState(2 * Math.PI * R);

    const hoverRef = React.useRef<number | null>(null);
    hoverRef.current = hover;

    React.useLayoutEffect(() => {
        const len = trackRef.current?.getTotalLength?.();
        if (len) setTotal(len);
    }, []);

    React.useEffect(() => {
        if (inView) setEntered(true);
    }, [inView]);

    /* Kept apart from the effect that sets `entered`: sharing one would tear
       the timer down on the very re-render that starts it. */
    React.useEffect(() => {
        if (!entered) return;
        if (!motionOK) {
            setDrawn(true);
            return;
        }
        const id = window.setTimeout(() => setDrawn(true), DRAW_MS);
        return () => window.clearTimeout(id);
    }, [entered, motionOK]);

    /* One clock drives both the travelling head and the highlighted stage, so
       they can never drift apart. Hovering a label retargets that same clock
       instead of running a second one. */
    const running = drawn && inView && motionOK;
    const arcLen = total * ARC_FRACTION;

    React.useEffect(() => {
        if (!running) return;
        const arc = arcRef.current;
        if (!arc) return;
        let raf = 0;
        let last = 0;
        let p = 0;

        const frame = (now: number) => {
            const dt = last ? Math.min(now - last, 64) : 0;
            last = now;

            const target = hoverRef.current;
            if (target === null) {
                p = (p + dt / ORBIT_MS) % 1;
            } else {
                const d = wrapDelta(p, STAGE_AT[target]);
                /* Exponential approach: fast at first, no overshoot, settles. */
                p = (p + d * Math.min(1, dt / 180) + 1) % 1;
            }

            arc.style.strokeDashoffset = `${(arcLen - total * p).toFixed(2)}px`;
            const next = target === null ? stageAtProgress(p) : target;
            setActive((cur) => (cur === next ? cur : next));
            raf = requestAnimationFrame(frame);
        };

        raf = requestAnimationFrame(frame);
        return () => cancelAnimationFrame(raf);
    }, [running, arcLen, total]);

    const arcStyle = {
        strokeDasharray: `${arcLen.toFixed(2)}px ${(total - arcLen).toFixed(2)}px`,
        strokeDashoffset: `${arcLen.toFixed(2)}px`,
        opacity: drawn ? 1 : 0,
    } as React.CSSProperties;

    const trackStyle = motionOK
        ? ({
              strokeDasharray: `${total.toFixed(2)}px`,
              strokeDashoffset: entered ? 0 : `${total.toFixed(2)}px`,
          } as React.CSSProperties)
        : undefined;

    const show = !motionOK || entered;
    const at = (ms: number) => ({ duration: DUR.base, ease: EASE_OUT, delay: ms / 1000 });

    return (
        <div className="ds-ring-wrap" ref={ref}>
            <div className="ds-ring">
                <svg className="ds-ring__svg" viewBox="0 0 100 100" aria-hidden="true">
                    <path className="ds-ring__track" ref={trackRef} d={RING_PATH} style={trackStyle} />
                    <path className="ds-ring__arc" ref={arcRef} d={RING_PATH} style={arcStyle} />
                </svg>

                {LOOP_STAGES.map((stage, i) => {
                    const on = i === active;
                    const cls = (base: string) => [base, on ? `${base}--on` : ''].filter(Boolean).join(' ');
                    return (
                        <React.Fragment key={stage.name}>
                            <motion.span
                                className={cls('ds-ring__dot')}
                                style={polar(stage.angle, 0)}
                                initial={{ opacity: 0, scale: 0.2 }}
                                animate={show ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.2 }}
                                transition={at(NODE_MS(i))}
                                aria-hidden="true"
                            />
                            <motion.div
                                className={cls('ds-ring__node')}
                                style={{ ...polar(stage.angle, LABEL_OFFSETS[i]), '--tilt': `${LABEL_TILT[i]}deg` } as React.CSSProperties}
                                initial={{ opacity: 0, y: 12 }}
                                animate={show ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
                                transition={{ ...at(NODE_MS(i)), duration: DUR.slow }}
                                onPointerEnter={() => setHover(i)}
                                onPointerLeave={() => setHover(null)}
                            >
                                <span className={cls('ds-ring__name')}>{stage.name}</span>
                                <span className="ds-ring__desc">{stage.desc}</span>
                            </motion.div>
                        </React.Fragment>
                    );
                })}

                {/* Two margin doodles, inside the ring on the same radial line as the
                stage they belong to. Hidden with the labels on narrow screens. */}
                <motion.div
                    className="ds-ring__doodleslot"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: show ? 1 : 0 }}
                    transition={{ duration: DUR.glass, ease: EASE_OUT, delay: DOODLE_MS / 1000 }}
                >
                    <DoodleSim className="ds-ring__doodle" style={polar(-10, -11)} />
                    <DoodleGlass className="ds-ring__doodle" style={polar(70, -11)} />
                </motion.div>

                <motion.div
                    className="ds-ring__center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: show ? 1 : 0 }}
                    transition={{ duration: DUR.glass, ease: EASE_OUT, delay: CENTER_MS / 1000 }}
                >
                    <p className="ds-ring__centertext">The moment something shifts, the entire loop fires again.</p>
                    <span className="ds-ring__note">continuous, autonomous</span>
                </motion.div>
            </div>

            {/* Narrow screens drop the labels off the ring and read them as a list. */}
            <ol className="ds-ring__list">
                {LOOP_STAGES.map((stage, i) => {
                    const on = i === active;
                    return (
                        <li key={stage.name} className={['ds-ring__item', on ? 'ds-ring__item--on' : ''].filter(Boolean).join(' ')}>
                            <span className={['ds-ring__dot', on ? 'ds-ring__dot--on' : ''].filter(Boolean).join(' ')} aria-hidden="true" />
                            <span className={['ds-ring__name', on ? 'ds-ring__name--on' : ''].filter(Boolean).join(' ')}>{stage.name}</span>
                            <span className="ds-ring__desc">{stage.desc}</span>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}
